import React, { useEffect } from 'react';
import { useLatest } from 'ahooks';

import type { EChartsType } from 'echarts';
import type { EChartsOption } from 'echarts/types/dist/shared';

/**
 * setOption方法的第二个参数类型，具体定义参见echarts官网
 */
export interface ISetOptionConfigs {
  /**
   * 是否不跟之前设置的 option 进行合并。默认为 false
   * 如果为 false，即表示合并
   * 如果为 true，表示所有组件都会被删除，然后根据新 option 创建所有新组件
   */
  notMerge?: boolean;
  /**
   * 在设置完 option 后是否不立即更新图表，默认为 false
   * 如果为 false，即同步立即更新
   * 如果为 true，则会在下一个 animation frame 中，才更新图表
   */
  lazyUpdate?: boolean;
  /**
   * 阻止调用 setOption 时抛出事件，默认为 false，即抛出事件。
   */
  silent?: boolean;
}

/**
 * echarts配置项变化时,更新echarts图表
 * @param chartInstance echarts图表实例
 * @param option echarts配置项
 * @param setOptionConfigs setOption方法的第二个参数，具体定义参见echarts官网
 * @param setOptionConfigs.notMerge 是否不跟之前设置的 option 进行合并。默认为 true
 * @param setOptionConfigs.lazyUpdate 在设置完 option 后是否不立即更新图表，默认为 false
 * @param setOptionConfigs.silent 阻止调用 setOption 时抛出事件，默认为 false
 */
export default function useChartRefreshWhenOptionsChanged<T extends EChartsOption>(
  chartInstance: React.RefObject<EChartsType>,
  option: T,
  setOptionConfigs?: ISetOptionConfigs,
) {
  const setOptionConfigsLatest = useLatest(setOptionConfigs);

  useEffect(() => {
    if (!chartInstance?.current) return;

    const notMerge = setOptionConfigsLatest?.current?.notMerge ?? true;
    const lazyUpdate = setOptionConfigsLatest?.current?.lazyUpdate ?? false;
    const silent = setOptionConfigsLatest?.current?.silent ?? false;

    chartInstance.current.setOption(option, { notMerge, lazyUpdate, silent });
  }, [option]);
}
